import { WIDGET_UTM_MEDIUM } from '../defaults';
import type { EmbedConfig, WidgetSchema } from '../types';
import { buildEmbedConfig } from './buildEmbedConfig';

export type EmbedFormat = 'script' | 'iframe';

/**
 * Whether a widget has a script embed as well as an iframe.
 *
 * `direct-iframe` widgets (KPOW, the broadcast view, the compute curve) do not: the loader
 * script only knows the event and banner paths, and it is a frozen unversioned file that
 * partners already have cached, so teaching it a new path is not an option. Offering a format
 * toggle for them rendered the same iframe under both labels.
 */
export function supportsScriptEmbed(schema: WidgetSchema): boolean {
    return schema.embed.kind !== 'direct-iframe';
}

/**
 * Single source of embed-code generation for every widget. Faithfully
 * reproduces the legacy `EmbedCodePlain` output for the event-loader case (so
 * existing partner embeds stay byte-for-byte valid — see buildEmbedCode.vitest),
 * and brings ticker-tape (`banner-loader`) and KPOW (`direct-iframe`) into the
 * same generator. Pure: no React, no `window` (origin is passed in).
 */
export function buildEmbedCode(
    schema: WidgetSchema,
    config: Record<string, unknown>,
    format: EmbedFormat,
    origin: string,
): string {
    const embed = buildEmbedConfig(schema, config);
    switch (schema.embed.kind) {
        case 'event-loader':
            return buildEventLoaderCode(embed, format, origin);
        case 'banner-loader':
            return buildBannerCode(embed, format, origin);
        case 'direct-iframe':
            return buildDirectIframeCode(schema, embed, origin);
    }
}

// --- event-loader (faithful port of EmbedCodePlain) -------------------------

function buildEventLoaderCode(embed: EmbedConfig, format: EmbedFormat, origin: string): string {
    const isInline = embed.widgetSize === 'inline';
    const actualWidgetSize = isInline ? 'small' : embed.widgetSize;
    const displayWidth = embed.width || '100%';
    const displayHeight = embed.height || '100%';

    // Ticker tape carries colorTheme inside marketIds, not as a separate field.
    const isTickerTape = embed.widgetSize === 'ticker' && embed.variant === 'tape';
    const shouldIncludeColorTheme = !isTickerTape;

    if (format === 'iframe') {
        const params = new URLSearchParams();
        params.set('widget_size', actualWidgetSize || 'large');
        params.set('period', 'all');
        if (embed.colorTheme === 'dark') params.set('colorTheme', 'dark');
        if (embed.variant) params.set('variant', embed.variant);
        if (embed.marketId) params.set('market_id', embed.marketId);
        if (embed.marketIds) params.set('market_ids', embed.marketIds);
        if (embed.seriesTicker) params.set('series_ticker', embed.seriesTicker);
        if (embed.useMostRecentInSeries) params.set('use_most_recent_in_series', 'true');
        if (embed.utmSource) params.set('utm_source', embed.utmSource);
        params.set('utm_medium', WIDGET_UTM_MEDIUM);
        if (embed.utmCampaign) params.set('utm_campaign', embed.utmCampaign);
        if (embed.promoCode) params.set('referral', embed.promoCode);
        const heightPixels = parsePixelValue(displayHeight);
        const widthPixels = parsePixelValue(displayWidth);
        if (heightPixels) params.set('height', heightPixels);
        if (widthPixels) params.set('width', widthPixels);

        const src = `${origin}/external-widgets/events/${encodeURIComponent(embed.eventTicker)}?${params.toString()}`;
        return (
            `<!-- Kalshi Event Widget (iframe) BEGIN -->\n` +
            `<iframe\n  src="${src}"\n  style="width:${displayWidth};height:${displayHeight};${iframeChrome(embed)}"\n` +
            `  title="${embed.eventTicker} chart"\n  loading="lazy"\n></iframe>\n` +
            `<!-- Kalshi Event Widget (iframe) END -->`
        );
    }

    if (isInline) {
        const inlineUtm = [
            embed.utmSource ? `,"utmSource": "${embed.utmSource}"` : '',
            embed.utmCampaign ? `,"utmCampaign": "${embed.utmCampaign}"` : '',
            embed.promoCode ? `,"promoCode": "${embed.promoCode}"` : '',
        ].join('');
        return (
            `<span class="kalshi-embed-container" style="display:inline-block;vertical-align:middle;line-height:1;width:auto;">` +
            `<span class="kalshi-embed-container__widget" style="display:inline-block;width:auto;height:auto;"></span>` +
            `<script type="text/javascript" src="${origin}/js/kalshi-embed-event.js">` +
            `{"eventTicker": "${embed.eventTicker}"${shouldIncludeColorTheme ? `,"colorTheme": "${embed.colorTheme}"` : ''},"widgetSize": "small"` +
            `${embed.marketId ? `,"marketId": "${embed.marketId}"` : ''}` +
            `${embed.marketIds ? `,"marketIds": "${embed.marketIds}"` : ''}` +
            `${embed.variant ? `,"variant": "${embed.variant}"` : ''}` +
            `${inlineUtm}}</script></span>`
        );
    }

    const additional = [
        actualWidgetSize ? `"widgetSize": "${actualWidgetSize}"` : '',
        embed.variant ? `"variant": "${embed.variant}"` : '',
        embed.marketId ? `"marketId": "${embed.marketId}"` : '',
        embed.marketIds ? `"marketIds": "${embed.marketIds}"` : '',
        embed.seriesTicker ? `"seriesTicker": "${embed.seriesTicker}"` : '',
        embed.useMostRecentInSeries ? `"useMostRecentInSeries": true` : '',
        embed.utmSource ? `"utmSource": "${embed.utmSource}"` : '',
        embed.utmCampaign ? `"utmCampaign": "${embed.utmCampaign}"` : '',
        embed.promoCode ? `"promoCode": "${embed.promoCode}"` : '',
    ]
        .filter(Boolean)
        .join(',\n    ');

    return (
        `<!-- Kalshi Event Widget BEGIN -->\n` +
        `<div class="kalshi-embed-container" style="width:${displayWidth};height:${displayHeight};overflow:hidden;${containerChrome(embed)}">\n` +
        `  <div class="kalshi-embed-container__widget" style="width:100%;height:100%;"></div>\n` +
        `  <script type="text/javascript" src="${origin}/js/kalshi-embed-event.js">\n` +
        `  {\n    "eventTicker": "${embed.eventTicker}"${shouldIncludeColorTheme ? `,\n    "colorTheme": "${embed.colorTheme}"` : ''},\n` +
        `    "height": "${displayHeight}",\n    "width": "${displayWidth}",\n    "period": "all"` +
        `${additional ? `,\n    ${additional}` : ''}\n  }\n  </script>\n</div>\n` +
        `<!-- Kalshi Event Widget END -->`
    );
}

// --- banner-loader (ticker tape) --------------------------------------------

function buildBannerCode(embed: EmbedConfig, format: EmbedFormat, origin: string): string {
    const params = new URLSearchParams();
    if (embed.category) params.set('category', embed.category);
    params.set('colorTheme', embed.colorTheme);
    if (embed.logoColor) params.set('logocolor', embed.logoColor);
    if (embed.showLogo === false) params.set('showLogo', 'false');
    if (embed.utmSource) params.set('utm_source', embed.utmSource);
    params.set('utm_medium', WIDGET_UTM_MEDIUM);
    if (embed.utmCampaign) params.set('utm_campaign', embed.utmCampaign);
    if (embed.promoCode) params.set('referral', embed.promoCode);
    const src = `${origin}/banner?${params.toString()}`;
    const height = parsePixelValue(embed.height) ?? '60';

    if (format === 'script') {
        // Loader ticker path: widgetSize 'ticker' + variant 'tape'. The loader
        // packs logoColor/colorTheme into marketIds for backwards compatibility.
        const marketIds = [embed.logoColor ? `logoColor:${embed.logoColor}` : '', `colorTheme:${embed.colorTheme}`]
            .filter(Boolean)
            .join(',');
        return (
            `<!-- Kalshi Ticker Tape BEGIN -->\n` +
            `<div class="kalshi-embed-container">\n` +
            `  <div class="kalshi-embed-container__widget"></div>\n` +
            `  <script type="text/javascript" src="${origin}/js/kalshi-embed-event.js">\n` +
            `  {\n    "eventTicker": "${embed.category ?? ''}",\n    "category": "${embed.category ?? ''}",\n    "widgetSize": "ticker",\n    "variant": "tape",\n` +
            `    "marketIds": "${marketIds}"` +
            `${embed.showLogo === false ? ',\n    "showLogo": false' : ''}` +
            `${embed.utmSource ? `,\n    "utmSource": "${embed.utmSource}"` : ''}` +
            `${embed.utmCampaign ? `,\n    "utmCampaign": "${embed.utmCampaign}"` : ''}` +
            `${embed.promoCode ? `,\n    "promoCode": "${embed.promoCode}"` : ''}\n  }\n  </script>\n</div>\n` +
            `<!-- Kalshi Ticker Tape END -->`
        );
    }

    return (
        `<!-- Kalshi Ticker Tape (iframe) BEGIN -->\n` +
        `<iframe\n  src="${src}"\n  style="width:100%;height:${height}px;${iframeChrome(embed)}"\n` +
        `  title="Kalshi ticker tape"\n  loading="lazy"\n></iframe>\n` +
        `<!-- Kalshi Ticker Tape (iframe) END -->`
    );
}

// --- direct-iframe (KPOW) ---------------------------------------------------

function buildDirectIframeCode(schema: WidgetSchema, embed: EmbedConfig, origin: string): string {
    const params = new URLSearchParams();
    // Widget-specific choices (e.g. chip=b200) come first so the src reads naturally.
    for (const [key, value] of Object.entries(embed.iframeParams ?? {})) params.set(key, value);
    if (embed.colorTheme === 'dark') params.set('colorTheme', 'dark');
    if (embed.utmSource) params.set('utm_source', embed.utmSource);
    if (embed.utmCampaign) params.set('utm_campaign', embed.utmCampaign);
    if (embed.promoCode) params.set('referral', embed.promoCode);
    const query = params.toString();
    const src = `${origin}${schema.embed.iframePath ?? ''}${query ? `?${query}` : ''}`;
    const width = embed.width || '100%';
    const height = embed.height || '560px';
    return (
        `<iframe\n  src="${src}"\n  style="width:${width};height:${height};${iframeChrome(embed)}"\n` +
        `  title="${schema.meta.title}"\n  loading="lazy"\n></iframe>`
    );
}

// Optional partner-controlled chrome on the embed's outer element. Off by default so
// widgets stay edge-to-edge and the embedder owns rounding/borders.
function iframeChrome(embed: EmbedConfig): string {
    return (
        (embed.showBorder ? 'border:1px solid rgba(0,0,0,0.12);' : 'border:none;') +
        (embed.rounded ? 'border-radius:12px;' : '')
    );
}

function containerChrome(embed: EmbedConfig): string {
    return (
        (embed.showBorder ? 'border:1px solid rgba(0,0,0,0.12);' : '') + (embed.rounded ? 'border-radius:12px;' : '')
    );
}

function parsePixelValue(value: string): string | null {
    const match = value.match(/^(\d+)px$/i);
    return match ? match[1] : null;
}
